



































































































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import List from '@/components/util/List.vue';
import {
  Asset,
  AssetsApi,
  ApiAssetsGetRequest,
  AssetPagedCollectionApiResponse
} from 'performo-api';
import { mapGetters } from 'vuex';
import { User } from 'oidc-client';

import { SortChangedEvent } from '@/models/SortChangedEvent';
import { handleApiError } from '@/ApiHelpers';

@Component({
  components: {
    List
  },
  computed: mapGetters({
    currentUser: 'currentUser',
    api: 'assetsApi'
  })
})
export default class AssetPicker extends Vue {
  @Prop()
  picks!: Asset[];

  @Prop({ required: true, type: String })
  private propertyId!: string;

  private currentUser!: User;

  private api!: AssetsApi;

  private internalTake = 25;

  private cols = [
    { displayName: 'Code', columnName: 'assetCode', width: 'auto' },
    { displayName: 'Property', columnName: 'propertyId', width: 'auto' },
    { displayName: 'Name', columnName: 'name', width: '200px' },
    { displayName: 'Type', columnName: 'type', width: 'auto' },
    { displayName: 'Building', columnName: 'location1', width: 'auto' },
    { displayName: 'Floor', columnName: 'floor', width: '80px' },
    { displayName: 'Room', columnName: 'location2', width: 'auto' },
    { displayName: 'Selected', columnName: 'selected', width: '80px' }
  ];

  private pageNumber = 1;

  get assets(): Asset[] {
    if (this.pagedAssets) {
      return this.pagedAssets.data || [];
    }
    return [];
  }

  private autoCompleteQuery = '';
  private autoCompleteAssets!: AssetPagedCollectionApiResponse;

  private pagedAssets!: AssetPagedCollectionApiResponse;

  private showModal = false;

  private picksContainsAsset(asset: Asset) {
    if (!this.picks) return false;
    const contains =
      this.picks.findIndex((f) => f.assetId === asset.assetId) >= 0;
    return contains;
  }

  private query!: ApiAssetsGetRequest;

  private itemClicked(item: Asset) {
    const pickedIndex = this.picks.findIndex((f) => f.assetId == item.assetId);
    if (pickedIndex === -1) {
      // add if not found
      this.$emit('update:picks', this.picks.concat(item));
    } else {
      // remove if found in the picked list
      this.$emit(
        'update:picks',
        this.picks.filter((f) => f.assetId != item.assetId)
      );
    }
  }

  data() {
    // this lets the undefined query! refs become reactive
    // https://class-component.vuejs.org/guide/class-component.html#data
    return {
      query: undefined,
      pagedAssets: undefined,
      autoCompleteAssets: undefined
    };
  }

  private sortChangedHandler(sort: SortChangedEvent) {
    // this will trigger the watcher, so we don't need to call it explicitly.
    this.query = {
      ...this.query,
      sortColumn: sort.column,
      sortDirection: sort.direction === 1 ? 'ascend' : 'descend'
    };
  }

  private paginationChangeHandler(pageNumber: number) {
    this.pageNumber = pageNumber;

    this.fetchAssets();
  }

  @Watch('query', { deep: true })
  private async queryChanged(val: ApiAssetsGetRequest) {
    if (val) {
      this.pageNumber = 1;
      this.fetchAssets();
    }
  }

  @Watch('autoCompleteQuery', { immediate: true })
  private async autoCompleteQueryChanged(val: string) {
    const query = {
      assetCode: val,
      propertyId: this.propertyId,
      skip: 0,
      take: 250,
      sortColumn: 'assetCode',
      sortDirection: 'ascend'
    };
    try {
      this.autoCompleteAssets = await this.api.apiAssetsGet(query);
    } catch (error) {
      handleApiError(error);
    }
  }

  private async fetchAssets() {
    const query = {
      ...this.query,
      propertyId: this.propertyId,
      skip: (this.query.take || 25) * (this.pageNumber - 1) + 1,
      take: this.query.take ? this.query.take : this.internalTake,
      sortColumn: this.query.sortColumn !== '' ? this.query.sortColumn : '',
      sortDirection:
        this.query.sortDirection !== '' ? this.query.sortDirection : 'ascend'
    };
    try {
      this.pagedAssets = await this.api.apiAssetsGet(query);
    } catch (error) {
      handleApiError(error);
    }
  }

  async onQuickScannerScannedHandler(barcode: string) {
    try {
      // some customers may have their assets configured find the AssetCode with the barcode scanner
      // below we do an 'OR' find to retrieve the asset that: asset.barcode === barcode OR asset.assetCode === barcode
      const assets = await this.api.apiAssetsGet({ barcode: barcode });
      if (assets && assets.data && assets.data.length === 1) {
        // an asset with a matching barcode value was found
        this.$emit('update:picks', this.picks.concat(assets.data[0]));
      } else {
        // no matching asset for the barcode was found
        // search for asset with matching assetCode and return that instead
        const assets = await this.api.apiAssetsGet({ assetCode: barcode });
        if (assets && assets.data && assets.data.length === 1) {
          this.$emit('update:picks', this.picks.concat(assets.data[0]));
        }
      }
    } catch (error) {
      handleApiError(error);
    }
  }

  async onAssetCodeScannedHandler(barcode: string) {
    this.query.assetCode = barcode;
  }

  async onBarcodeScannedHandler(barcode: string) {
    this.query.barcode = barcode;
  }

  private async mounted() {
    // must specify properties on objects that are to be reactive even if blank
    // see also -- https://stackoverflow.com/a/46667367/86860
    this.query = {
      assetCode: '',
      sortColumn: 'assetCode',
      sortDirection: 'ascend',
      status: '',
      skip: 0,
      take: this.internalTake
    };

    this.fetchAssets();
  }
}
